/**
 * TestimonialsBlogBlock — Compact single-testimonial block for blog articles.
 *
 * Picks the highest-rated published testimonial (best social proof).
 * Renders nothing if there is no published review yet.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Quote, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TestimonialsBlogBlock() {
  const [item, setItem] = useState(null); // null=loading|empty, {...}=found

  useEffect(() => {
    axios.get(`${API_URL}/api/testimonials?limit=6`)
      .then((r) => {
        const list = r.data?.testimonials || [];
        if (list.length === 0) {
          setItem(null);
          return;
        }
        // Prefer highest rating, then most recent
        const sorted = [...list].sort((a, b) =>
          (b.rating || 0) - (a.rating || 0) ||
          new Date(b.created_at || 0) - new Date(a.created_at || 0)
        );
        setItem(sorted[0]);
      })
      .catch(() => setItem(null));
  }, []);

  if (!item) return null;

  return (
    <aside
      className="my-10 bg-[#302f2c] border-l-4 border-[#d9fb06] rounded-r-2xl rounded-l-md p-6 md:p-7 relative overflow-hidden"
      data-testid="testimonial-blog-block"
    >
      <Quote className="absolute -top-2 -right-2 text-[#d9fb06]/10" size={120} strokeWidth={1.5} />
      <p className="text-[10px] tracking-widest uppercase text-[#d9fb06] font-bold mb-3">
        Ils m&apos;ont fait confiance
      </p>
      <blockquote className="text-[#dfddd6] text-base md:text-lg leading-relaxed italic mb-4 relative z-10">
        « {item.content} »
      </blockquote>
      <div className="flex items-center justify-between border-t border-[#3f4816] pt-3 relative z-10">
        <div className="min-w-0">
          <p className="text-[#d9fb06] font-bold text-sm truncate">{item.author_name}</p>
          {item.author_role && (
            <p className="text-[#888680] text-xs truncate">{item.author_role}</p>
          )}
        </div>
        <div className="flex gap-0.5 flex-shrink-0" aria-label={`${item.rating || 5} sur 5`}>
          {[1, 2, 3, 4, 5].map((n) => (
            <Star
              key={n}
              size={14}
              className={n <= (item.rating || 5) ? 'text-[#d9fb06] fill-[#d9fb06]' : 'text-[#3f4816]'}
            />
          ))}
        </div>
      </div>
    </aside>
  );
}
